import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ShieldCheck } from "lucide-react";

const SCOPES = [["default", "Default (everyone)"], ["department", "Department"], ["team", "Team"], ["employee", "Employee"]];
const empty = () => ({ name: "", scope_type: "default", scope_id: "", require_location: false, require_network: false,
  require_qr: false, allow_exception: true, location_ids: [], network_ids: [], qr_config_ids: [], max_gps_accuracy_m: 100, active: true });

export default function PoliciesPanel() {
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [qrConfigs, setQrConfigs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const load = () => api.get("/hr/attendance/policies").then(r => setRows(r.data)).catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => {
    load();
    api.get("/hr/attendance/locations").then(r => setLocations(r.data)).catch(() => {});
    api.get("/hr/attendance/networks").then(r => setNetworks(r.data)).catch(() => {});
    api.get("/hr/attendance/qr-configs").then(r => setQrConfigs(r.data)).catch(() => {});
    api.get("/hr/employees").then(r => setEmployees(r.data)).catch(() => {});
    api.get("/hr/departments").then(r => setDepartments(r.data)).catch(() => {});
  }, []);

  const openEdit = (p) => { setEditing(p); setForm({ ...empty(), ...p, scope_id: p.scope_id || "", location_ids: p.location_ids || [], network_ids: p.network_ids || [], qr_config_ids: p.qr_config_ids || [] }); setOpen(true); };
  const toggleId = (field, id) => setForm(f => ({ ...f, [field]: f[field].includes(id) ? f[field].filter(x => x !== id) : [...f[field], id] }));
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const body = { ...form, scope_id: form.scope_type === "default" ? null : form.scope_id || null, max_gps_accuracy_m: parseFloat(form.max_gps_accuracy_m) || 100 };
    if (form.scope_type !== "default" && !body.scope_id) return toast.error("Select a scope target");
    try {
      if (editing) await api.put(`/hr/attendance/policies/${editing.id}`, body);
      else await api.post("/hr/attendance/policies", body);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete policy?")) return;
    try { await api.delete(`/hr/attendance/policies/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const scopeLabel = (p) => {
    if (p.scope_type === "default") return "Everyone";
    if (p.scope_type === "employee") return employees.find(e => e.employee_id === p.scope_id)?.full_name || p.scope_id;
    return departments.find(d => d.id === p.scope_id)?.name || p.scope_id;
  };

  return (
    <div data-testid="att-policies-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">Verification policies. Most specific wins: employee → team/department → default. Toggle which checks are required.</p>
        <Button data-testid="att-pol-new" onClick={() => { setEditing(null); setForm(empty()); setOpen(true); }} className="bg-sky-600 hover:bg-sky-700"><Plus className="w-4 h-4 mr-1.5" />Policy</Button>
      </div>
      <div className="space-y-2">
        {rows.map(p => (
          <div key={p.id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-center justify-between" data-testid={`att-pol-${p.id}`}>
            <div>
              <div className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-sky-600" />{p.name}</div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                <Badge variant="outline" className="text-xs">{SCOPES.find(s => s[0] === p.scope_type)?.[1]}: {scopeLabel(p)}</Badge>
                {p.require_location && <Badge variant="outline" className="text-xs bg-sky-50 text-sky-700 border-sky-200">Location required</Badge>}
                {p.require_network && <Badge variant="outline" className="text-xs bg-sky-50 text-sky-700 border-sky-200">Network required</Badge>}
                {p.require_qr && <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-200">QR required</Badge>}
                {p.allow_exception && <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">Exceptions allowed</Badge>}
                <Badge variant="outline" className={`text-xs ${p.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-400"}`}>{p.active ? "Active" : "Inactive"}</Badge>
              </div>
            </div>
            <span className="flex gap-2">
              <button onClick={() => openEdit(p)} data-testid={`att-pol-edit-${p.id}`}><Pencil className="w-4 h-4 text-slate-400" /></button>
              {p.scope_type !== "default" && <button onClick={() => del(p.id)}><Trash2 className="w-4 h-4 text-rose-500" /></button>}
            </span>
          </div>
        ))}
        {rows.length === 0 && <div className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-center text-slate-400 text-sm">No policies yet</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="att-pol-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit policy" : "New policy"}</DialogTitle>
            <DialogDescription>Choose which checks are required and who this applies to. Most specific scope wins: employee → team/department → default.</DialogDescription></DialogHeader>
          <div><Label>Policy Name *</Label><Input data-testid="att-pol-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Scope</Label>
              <Select value={form.scope_type} onValueChange={v => setForm({ ...form, scope_type: v, scope_id: "" })}>
                <SelectTrigger data-testid="att-pol-scope"><SelectValue /></SelectTrigger>
                <SelectContent>{SCOPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.scope_type !== "default" && (
              <div><Label>Target</Label>
                <Select value={form.scope_id} onValueChange={v => setForm({ ...form, scope_id: v })}>
                  <SelectTrigger data-testid="att-pol-scope-id"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {form.scope_type === "employee"
                      ? employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} · {e.full_name}</SelectItem>)
                      : departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.require_location} onCheckedChange={v => setForm({ ...form, require_location: v })} data-testid="att-pol-req-loc" />Require Location</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.require_network} onCheckedChange={v => setForm({ ...form, require_network: v })} data-testid="att-pol-req-net" />Require Office Network</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.require_qr} onCheckedChange={v => setForm({ ...form, require_qr: v })} data-testid="att-pol-req-qr" />Require Office QR</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.allow_exception} onCheckedChange={v => setForm({ ...form, allow_exception: v })} data-testid="att-pol-allow-exc" />Allow Exceptions</label>
          </div>
          {form.require_location && (
            <div><Label>Allowed Locations (blank = all active)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {locations.map(l => <button key={l.id} type="button" onClick={() => toggleId("location_ids", l.id)} className={`text-xs px-2 py-1 rounded border ${form.location_ids.includes(l.id) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-200"}`}>{l.name}</button>)}
                {locations.length === 0 && <span className="text-xs text-amber-600">No locations configured yet</span>}
              </div>
            </div>
          )}
          {form.require_network && (
            <div><Label>Allowed Networks (blank = all active)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {networks.map(n => <button key={n.id} type="button" onClick={() => toggleId("network_ids", n.id)} className={`text-xs px-2 py-1 rounded border ${form.network_ids.includes(n.id) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-200"}`}>{n.name}</button>)}
                {networks.length === 0 && <span className="text-xs text-amber-600">No networks configured yet</span>}
              </div>
            </div>
          )}
          {form.require_qr && (
            <div><Label>Accepted QR configs (blank = all active)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {qrConfigs.map(c => <button key={c.id} type="button" onClick={() => toggleId("qr_config_ids", c.id)} className={`text-xs px-2 py-1 rounded border ${form.qr_config_ids.includes(c.id) ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200"}`}>{c.name}</button>)}
                {qrConfigs.length === 0 && <span className="text-xs text-amber-600">No QR configs yet — add under QR Verification</span>}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 items-end">
            <div><Label>Max GPS Accuracy (m)</Label><Input type="number" data-testid="att-pol-accuracy" value={form.max_gps_accuracy_m} onChange={e => setForm({ ...form, max_gps_accuracy_m: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm pb-2"><Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} data-testid="att-pol-active" />Active</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="att-pol-save" className="bg-slate-900">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
